import { makeAuthenticatedRequest } from './authService';

export interface TranslationResponse {
  success: boolean;
  translatedText: string;
  error?: string;
}

/**
 * Translate text using Gemini AI
 * @param text - The text to translate
 * @param targetLanguage - The target language name (e.g., "English", "Spanish", "French")
 * @returns Promise with translated text
 */
export const translateText = async (
  text: string,
  targetLanguage: string
): Promise<TranslationResponse> => {
  try {
    // Validate input
    if (!text || !text.trim()) {
      return {
        success: false,
        translatedText: '',
        error: 'Text to translate cannot be empty'
      };
    }

    if (!targetLanguage || !targetLanguage.trim()) {
      return {
        success: false,
        translatedText: '',
        error: 'Target language must be specified'
      };
    }

    // Prepare the translation prompt
    const systemPrompt = 'You are a professional translator. Translate the given text accurately while preserving meaning, tone, and context. Return only the translated text without any additional explanation or commentary.';
    
    const userMessage = `Translate the following text to ${targetLanguage}:\n\n${text}`;

    // Call the AI API
    const response = await makeAuthenticatedRequest('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({
        provider: 'gemini',
        model: 'gemini-2.5-flash',
        systemPrompt,
        messages: [
          {
            role: 'user',
            content: userMessage
          }
        ],
        maxTokens: 2000,
        temperature: 0.3 // Lower temperature for more consistent translations
      })
    });

    // Extract the translated text from the response
    if (response.success && response.data) {
      let translatedText = '';
      
      // Try to get text from response field (string)
      if (response.data.response && typeof response.data.response === 'string') {
        translatedText = response.data.response.trim();
      }
      // Otherwise, try to extract from content array
      else if (response.data.content) {
        if (Array.isArray(response.data.content) && response.data.content.length > 0) {
          // Extract text from content array (e.g., [{type: "text", text: "..."}])
          const textContent = response.data.content.find((item: any) => item.type === 'text');
          if (textContent && textContent.text) {
            translatedText = textContent.text.trim();
          } else if (typeof response.data.content[0] === 'string') {
            translatedText = response.data.content[0].trim();
          }
        } else if (typeof response.data.content === 'string') {
          translatedText = response.data.content.trim();
        }
      }
      
      if (translatedText) {
        return {
          success: true,
          translatedText
        };
      } else {
        return {
          success: false,
          translatedText: '',
          error: 'Could not extract translated text from response'
        };
      }
    } else {
      return {
        success: false,
        translatedText: '',
        error: response.message || 'Translation failed'
      };
    }
  } catch (error: any) {
    console.error('Translation error:', error);
    
    // Handle specific error cases
    if (error.message?.includes('rate limit')) {
      return {
        success: false,
        translatedText: '',
        error: 'Translation service rate limit exceeded. Please try again later.'
      };
    }
    
    if (error.message?.includes('Authentication')) {
      return {
        success: false,
        translatedText: '',
        error: 'Authentication failed. Please log in again.'
      };
    }

    return {
      success: false,
      translatedText: '',
      error: error.message || 'Failed to translate text. Please try again.'
    };
  }
};

